import React from 'react';
import PropTypes from 'prop-types';
import { Skeleton } from 'antd-v5';
import { useHistory } from 'react-router-dom';

/**
 * EventKPICards component displays key performance indicators for an event
 */
const EventKPICards = ({
  event,
  totalAudience,
  recentContactPercentage,
  recentContactCount,
  salesAmount,
  isLoadingSales,
  eventId,
}) => {
  const history = useHistory();
  const audienceCount = totalAudience || event.audienceCount || 0;
  const contactedCount = recentContactCount ?? event.contactedCount ?? 0;
  const appointmentsCount = event.clientsWithAppointmentsCount || 0;
  const salesValue = salesAmount ?? 0;

  // Use recentContactPercentage from audience API if available, otherwise calculate
  const getContactedPercent = () => {
    if (recentContactPercentage != null) {
      return Math.round(recentContactPercentage);
    }
    if (audienceCount > 0) {
      return Math.round((contactedCount / audienceCount) * 100);
    }
    return 0;
  };
  const contactedPercent = getContactedPercent();

  const appointmentsPercent = audienceCount > 0 ? Math.round((appointmentsCount / audienceCount) * 100) : 0;

  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleSalesClick = () => {
    if (!eventId) return;
    const params = new URLSearchParams({ event: eventId });
    // Use only the YYYY-MM-DD portion so parseDateParam treats it as local midnight
    if (event.startDate) params.set('startDate', event.startDate.substring(0, 10));
    if (event.endDate) params.set('endDate', event.endDate.substring(0, 10));
    history.push(`/Reports/Appointments?${params.toString()}`);
  };

  return (
    <div className="event-kpi-cards">
      <div className="kpi-card">
        <div className="kpi-label">Audience</div>
        <div className="kpi-value">{audienceCount.toLocaleString()}</div>
      </div>

      <div className="kpi-card">
        <div className="kpi-label">Contacted last 7 days</div>
        <div className="kpi-value">
          {contactedCount.toLocaleString()}
          <span className="kpi-suffix">{contactedPercent}%</span>
        </div>
      </div>

      <div className="kpi-card">
        <div className="kpi-label">Appointments</div>
        <div className="kpi-value">
          {appointmentsCount.toLocaleString()}
          <span className="kpi-suffix">{appointmentsPercent}%</span>
        </div>
      </div>

      <div
        className={`kpi-card${eventId ? ' kpi-card--clickable' : ''}`}
        onClick={eventId ? handleSalesClick : undefined}
        role={eventId ? 'button' : undefined}
        tabIndex={eventId ? 0 : undefined}
        onKeyDown={eventId ? e => e.key === 'Enter' && handleSalesClick() : undefined}
        style={eventId ? { cursor: 'pointer' } : undefined}
      >
        <div className="kpi-label">Sales from appointments</div>
        <div className="kpi-value">
          {isLoadingSales ? (
            <Skeleton.Input active size="small" className="kpi-skeleton" />
          ) : (
            formatCurrency(salesValue)
          )}
        </div>
        {eventId && <div style={{ fontSize: '12px', color: '#1677ff', marginTop: '4px' }}>View in report →</div>}
      </div>
    </div>
  );
};

EventKPICards.propTypes = {
  event: PropTypes.shape({
    audienceCount: PropTypes.number,
    contactedCount: PropTypes.number,
    clientsWithAppointmentsCount: PropTypes.number,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  totalAudience: PropTypes.number,
  recentContactPercentage: PropTypes.number,
  recentContactCount: PropTypes.number,
  salesAmount: PropTypes.number,
  isLoadingSales: PropTypes.bool,
  eventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

EventKPICards.defaultProps = {
  event: {},
  totalAudience: 0,
  recentContactPercentage: null,
  recentContactCount: null,
  salesAmount: null,
  isLoadingSales: false,
  eventId: null,
};

export default EventKPICards;
